//src/components/main/ExternalLessons.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllExternalLessons,
  createExternalLesson,
  updateExternalLesson,
  deleteExternalLesson,
  bulkCreateExternalLessons,
  bulkUpdateExternalLessons,
  bulkDeleteExternalLessons,
  getAllExternalLessonClicks
} from '../apicalls/externalLessons'; // Assuming API calls are updated for new fields
import { isAuthenticated } from '../apicalls/auth';
import Autocomplete from 'react-google-autocomplete';
import { extractFromAddress } from '../utils'; // Assuming this utility exists
import { googleApiKey } from '../../constAPI'; // Assuming this constant exists
import Papa from 'papaparse';
// Helper function to format ISO datetime string to YYYY-MM-DD
const getLocalDate = (isoDateTime) => {
  if (!isoDateTime) return '';
  return new Date(isoDateTime).toISOString().split('T')[0];
};

// Helper function to format ISO datetime string to HH:MM
const getLocalTime = (isoDateTime) => {
  if (!isoDateTime) return '';
  const date = new Date(isoDateTime);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const ExternalLessons = () => {
  const [page, setPage] = useState(1);
const [perPage, setPerPage] = useState(20);
const [totalPages, setTotalPages] = useState(1);
const [total, setTotal] = useState(0);

const [q, setQ] = useState('');
const [levelFilter, setLevelFilter] = useState('');
const [statusFilter, setStatusFilter] = useState('');
const [dateFrom, setDateFrom] = useState('');
const [dateTo, setDateTo] = useState('');
const [sortBy, setSortBy] = useState('start_date_time');
const [sortDir, setSortDir] = useState('desc');

// Active tab: 'lessons' | 'clicks'
const [activeTab, setActiveTab] = useState('lessons');

// Clicks state
const [clicks, setClicks] = useState([]);
const [clicksPage, setClicksPage] = useState(1);
	const [clicksPerPage] = useState(20);
const [clicksTotalPages, setClicksTotalPages] = useState(1);
const [clicksTotal, setClicksTotal] = useState(0);
const [clicksLoading, setClicksLoading] = useState(false);
const [clicksLessonFilter, setClicksLessonFilter] = useState('');
const [clicksFrom, setClicksFrom] = useState('');
const [clicksTo, setClicksTo] = useState('');

  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // Stores the ID of the lesson being edited
    const [csvRows, setCsvRows] = useState([]);        // parsed rows ready to import
  const [csvErrors, setCsvErrors] = useState([]);    // parse/validation errors
  const [csvPreview, setCsvPreview] = useState([]);  // normalized preview
  const [importing, setImporting] = useState(false);
const [selectedIds, setSelectedIds] = useState([]);
const allSelected = lessons.length && selectedIds.length === lessons.length;

const toggleAll = () => {
  setSelectedIds(allSelected ? [] : lessons.map(l => l.id));
};
const toggleOne = (id) => {
  setSelectedIds(s => s.includes(id) ? s.filter(i => i !== id) : [...s, id]);
}
  // CSV → JSON normalizer: maps your CSV columns to API payload shape
  const normalizeCsvRow = (r) => {
    // r fields: title,start_date_time,end_date_time,location,latitude,longitude,external_url,status,lessontype_id,description,level,coach_name
    // description goes into metadata.description
    const asNumber = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);

    // If your CSV times are already UTC with Z (as in your example), KEEP as-is.
    const start = r.start_date_time?.trim();
    const end = r.end_date_time?.trim();

    // Basic validation
    const required = ['title','start_date_time','end_date_time','location','external_url','status','lessontype_id'];
    const missing = required.filter(k => !(r[k] || '').toString().trim());
    if (missing.length) {
      throw new Error(`Missing field(s): ${missing.join(', ')}`);
    }

    // metadata
    const coachName = (r.coach_name ?? r.coach ?? '').toString().trim();
    const metadata = {
      description: r.description ?? '',
      ...(coachName ? { full_name: coachName, coach_name: coachName } : {}),
    };
    return {
      title: r.title?.trim(),
      start_date_time: start,
      end_date_time: end,
      location: r.location?.trim(),
      latitude: r.latitude?.toString()?.trim() ? asNumber(r.latitude) : null,
      longitude: r.longitude?.toString()?.trim() ? asNumber(r.longitude) : null,
      level: r.level != null ? String(r.level).trim() : null,   
      external_url: r.external_url?.trim(),
      metadata,
      status: parseInt(r.status, 10),
      lessontype_id: parseInt(r.lessontype_id, 10),
    };
  };

  const handleCsvFile = (file) => {
    setCsvErrors([]);
    setCsvRows([]);
    setCsvPreview([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: h => h.trim().toLowerCase(), // be forgiving about header case/spaces
      complete: (results) => {
        const rows = [];
        const errors = [];

        results.data.forEach((row, idx) => {
          try {
            // Trim all fields
            const trimmed = Object.fromEntries(
              Object.entries(row).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
            );
            const normalized = normalizeCsvRow(trimmed);
            rows.push(normalized);
          } catch (e) {
            errors.push({ row: idx + 2, error: e.message }); // +2 for header + 1-based
          }
        });

        setCsvRows(rows);
        setCsvPreview(rows.slice(0, 10)); // show top 10 as preview
        setCsvErrors(errors);
      },
      error: (err) => setCsvErrors([{ row: 0, error: err.message }]),
    });
  };

  const importCsv = async () => {
    if (!csvRows.length) return alert('No valid rows to import.');
    setImporting(true);
    try {
      const res = await bulkCreateExternalLessons(csvRows, access_token);
      const { inserted = [], failed = [] } = res || {};
      let msg = `Imported ${inserted.length} lesson(s).`;
      if (failed.length) msg += ` ${failed.length} row(s) failed.`;
      alert(msg);
      setCsvRows([]);
      setCsvPreview([]);
      setCsvErrors([]);
      fetchLessons();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Bulk import failed');
    } finally {
      setImporting(false);
    }
  };
  const initialFormData = {
    title: '',
    coachName: '', 
    // UI helper fields for date/time input
    lessonDate: '', // YYYY-MM-DD
    lessonStartTime: '', // HH:MM
    lessonEndTime: '',   // HH:MM
    // location, latitude, longitude are handled by Autocomplete and direct input
    location: '',
    latitude: '',
    longitude: '',
    external_url: '',
    level: '',
    metadata: '{}',
    status: 0, // Default from schema
    lessontype_id: 3 // Default from schema
  };
  const [formData, setFormData] = useState(initialFormData);

  const { access_token } = isAuthenticated();

  const statusOptions = [
    { value: 0, label: 'New' },
    { value: 1, label: 'Confirmed' },
    { value: 2, label: 'Cancelled' },
    // Add more statuses as defined by your application logic
  ];

  const fetchLessons = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        q: q || undefined,
        level: levelFilter || undefined,
        status: statusFilter !== '' ? statusFilter : undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      const res = await getAllExternalLessons(access_token, params);
      setLessons(res?.data || []);
      setTotal(res?.pagination?.total || 0);
      setTotalPages(res?.pagination?.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
      setLessons([]);
      setTotal(0);
      setTotalPages(1);
    }
    setLoading(false);
  }, [access_token, page, perPage, q, levelFilter, statusFilter, dateFrom, dateTo, sortBy, sortDir]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  // Fetch clicks when on the clicks tab
  const fetchClicks = useCallback(async () => {
    setClicksLoading(true);
    try {
      const params = {
        page: clicksPage,
        per_page: clicksPerPage,
      };
      if (clicksLessonFilter) params.lesson_id = clicksLessonFilter;
      if (clicksFrom) params.from = clicksFrom;
      if (clicksTo) params.to = clicksTo;
      const res = await getAllExternalLessonClicks(access_token, params);
      setClicks(res?.data || []);
      setClicksTotal(res?.pagination?.total || 0);
      setClicksTotalPages(res?.pagination?.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch clicks:", error);
      setClicks([]);
      setClicksTotal(0);
      setClicksTotalPages(1);
    }
    setClicksLoading(false);
  }, [access_token, clicksPage, clicksPerPage, clicksLessonFilter, clicksFrom, clicksTo]);

  useEffect(() => {
    if (activeTab === 'clicks') {
      fetchClicks();
    }
  }, [fetchClicks, activeTab]);
const handleSort = (col) => {
  setPage(1);
  setSortBy(prev => {
    if (prev === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
      return prev;
    }
    setSortDir('asc');
    return col;
  });
};
const sortIcon = (col) => sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '';


  const handleChange = e => {
    const { name, value, type } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: type === 'number' ? parseInt(value, 10) : value
    }));
  };

  const handleSave = async () => {
    let metadataObj = {};
    try {
      metadataObj = JSON.parse(formData.metadata);
    } catch {
      alert('Metadata must be valid JSON');
      return;
    }
    metadataObj.full_name = formData.coachName;
    if (!formData.lessonDate || !formData.lessonStartTime || !formData.lessonEndTime) {
      alert('Please fill in the date, start time, and end time.');
      return;
    }

    // Construct start_date_time and end_date_time in ISO format (UTC)
    // Assumes lessonDate, lessonStartTime, lessonEndTime are in local time
    const start_date_time = new Date(`${formData.lessonDate}T${formData.lessonStartTime}`).toISOString();
    const end_date_time = new Date(`${formData.lessonDate}T${formData.lessonEndTime}`).toISOString();

    const payload = {
      title: formData.title,
      start_date_time, // Combined and formatted
      end_date_time,   // Combined and formatted
      location: formData.location,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      external_url: formData.external_url,
      metadata: metadataObj,
      level: formData.level?.trim() || null,
      status: parseInt(formData.status, 10), // Ensure status is an integer
      lessontype_id: parseInt(formData.lessontype_id, 10) // Ensure lessontype_id is an integer
    };

    try {
      if (editing) {
        await updateExternalLesson(editing, payload, access_token);
      } else {
        await createExternalLesson(payload, access_token);
      }
      resetForm();
      fetchLessons();
    } catch (error) {
      console.error("Failed to save lesson:", error);
      alert(`Error saving lesson: ${error.message || 'Unknown error'}`);
    }
  };

  const handleEdit = lesson => {
    setEditing(lesson.id);
    setFormData({
      title: lesson.title,
      coachName: lesson.metadata?.full_name || '',
      lessonDate: getLocalDate(lesson.start_date_time),
      lessonStartTime: getLocalTime(lesson.start_date_time),
      lessonEndTime: getLocalTime(lesson.end_date_time),
      location: lesson.location,
      latitude: lesson.latitude ?? '',
      longitude: lesson.longitude ?? '',
      external_url: lesson.external_url,
      level: lesson.level || '',
      metadata: JSON.stringify(lesson.metadata || {}, null, 2),
      status: lesson.status,
      lessontype_id: lesson.lessontype_id
    });
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      try {
        await deleteExternalLesson(id, access_token);
        fetchLessons();
      } catch (error) {
        console.error("Failed to delete lesson:", error);
        alert(`Error deleting lesson: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData(initialFormData);
  };

  if (loading) return <div className="p-4">Loading external lessons…</div>;

  return (
    <main className="external-lessons-container container my-4">
      <h2 className="mb-4">Manage External Lessons</h2>
      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'lessons' ? 'active' : ''}`}
            onClick={() => setActiveTab('lessons')}
          >
            Lessons
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'clicks' ? 'active' : ''}`}
            onClick={() => setActiveTab('clicks')}
          >
            Clicks
          </button>
        </li>
      </ul>

      {/* Clicks Tab */}
      {activeTab === 'clicks' && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>Lesson Clicks</span>
            <span className="text-muted">{clicksTotal} total clicks</span>
          </div>
          <div className="card-body">
            {/* Clicks filters */}
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">Filter by Lesson ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. 42"
                  value={clicksLessonFilter}
                  onChange={(e) => { setClicksLessonFilter(e.target.value); setClicksPage(1); }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">From Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={clicksFrom}
                  onChange={(e) => { setClicksFrom(e.target.value); setClicksPage(1); }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={clicksTo}
                  onChange={(e) => { setClicksTo(e.target.value); setClicksPage(1); }}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => { setClicksLessonFilter(''); setClicksFrom(''); setClicksTo(''); setClicksPage(1); }}
                >
                  Clear
                </button>
              </div>
            </div>

            {clicksLoading ? (
              <div className="text-center py-4">Loading clicks…</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Lesson ID</th>
                        <th>User ID</th>
                        <th>Clicked At</th>
                        <th>Device</th>
                        <th>Country</th>
                        <th>UTM Source</th>
                        <th>Session ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clicks.length > 0 ? clicks.map((click) => (
                        <tr key={click.id}>
                          <td>{click.id}</td>
                          <td>{click.external_lesson_id}</td>
                          <td>{click.user_id ?? '-'}</td>
                          <td>{click.clicked_at ? new Date(click.clicked_at).toLocaleString() : '-'}</td>
                          <td>{click.device_type ?? '-'}</td>
                          <td>{click.country ?? '-'}</td>
                          <td>{click.utm_source ?? '-'}</td>
                          <td>{click.session_id ?? '-'}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={8} className="text-center">No clicks recorded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Clicks pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Page {clicksPage} of {clicksTotalPages} • {clicksTotal} total
                  </div>
                  <div className="btn-group">
                    <button
                      className="btn btn-outline-secondary"
                      disabled={clicksPage <= 1}
                      onClick={() => setClicksPage(p => Math.max(p - 1, 1))}
                    >
                      Prev
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      disabled={clicksPage >= clicksTotalPages}
                      onClick={() => setClicksPage(p => Math.min(p + 1, clicksTotalPages))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lessons Tab */}
      {activeTab === 'lessons' && (
        <>
{/* CSV Import Card */}
      <div className="card mb-4">
        <div className="card-header">Import from CSV</div>
        <div className="card-body">
          <p className="text-muted mb-2">
            Expected headers: <code>title,start_date_time,end_date_time,location,latitude,longitude,external_url,status,lessontype_id,description,level,coach_name</code>
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            className="form-control mb-3"
            onChange={(e) => e.target.files?.[0] && handleCsvFile(e.target.files[0])}
          />

          {csvErrors.length > 0 && (
            <div className="alert alert-warning">
              <strong>Found {csvErrors.length} issue(s):</strong>
              <ul className="mb-0">
                {csvErrors.slice(0, 10).map((er, i) => (
                  <li key={i}>Row {er.row}: {er.error}</li>
                ))}
              </ul>
              {csvErrors.length > 10 && <div>…and more</div>}
            </div>
          )}

          {csvPreview.length > 0 && (
            <>
              <div className="table-responsive">
                
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Location</th>
                      <th>Lat</th>
                      <th>Lng</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>URL</th>
                      <th>Description</th>
                      <th>Level</th>
                      <th>Coach Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((r, i) => (
                      <tr key={i}>
                        <td>{r.title}</td>
                        <td>{r.start_date_time}</td>
                        <td>{r.end_date_time}</td>
                        <td>{r.location}</td>
                        <td>{r.latitude ?? ''}</td>
                        <td>{r.longitude ?? ''}</td>
                        <td>{r.status}</td>
                        <td>{r.lessontype_id}</td>
                        <td style={{maxWidth: 220, wordBreak:'break-all'}}>{r.external_url}</td>
                        <td style={{maxWidth: 280, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                          {r.metadata?.description}
                        </td>
                        <td>{r.level}</td>
                        <td>{r.metadata?.full_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                className="btn btn-success"
                disabled={importing}
                onClick={importCsv}
                title={csvRows.length ? `Import ${csvRows.length} lessons` : 'Nothing to import'}
              >
                {importing ? 'Importing…' : `Import ${csvRows.length} lessons`}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="card mb-5">
        <div className="card-header">
          {editing ? `Edit Lesson (ID: ${editing})` : 'Create New Lesson'}
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Title */}
            <div className="col-md-6">
              <label htmlFor="title" className="form-label">Title</label>
              <input
                type="text" id="title" className="form-control"
                name="title" value={formData.title} onChange={handleChange} required
              />
            </div>
            {/* Coach Name */}
            <div className="col-md-6">
              <label htmlFor="coachName" className="form-label">Coach Name</label>
              <input
                type="text"
                id="coachName"
                name="coachName"
                className="form-control"
                value={formData.coachName}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-4">
            <label htmlFor="level" className="form-label">Level</label>
            <input
              type="text" id="level" name="level" className="form-control"
              placeholder='e.g. 3.5 or 3.75-4.0'
              value={formData.level} onChange={handleChange}
            />
          </div>
            {/* External URL */}
            <div className="col-md-6">
              <label htmlFor="external_url" className="form-label">Booking URL</label>
              <input
                type="url" id="external_url" className="form-control"
                name="external_url" value={formData.external_url} onChange={handleChange} required
              />
            </div>
            
            {/* Date */}
            <div className="col-md-4">
              <label htmlFor="lessonDate" className="form-label">Date</label>
              <input
                type="date" id="lessonDate" className="form-control"
                name="lessonDate" value={formData.lessonDate} onChange={handleChange} required
              />
            </div>

            {/* Start Time */}
            <div className="col-md-4">
              <label htmlFor="lessonStartTime" className="form-label">Start Time</label>
              <input
                type="time" id="lessonStartTime" className="form-control"
                name="lessonStartTime" value={formData.lessonStartTime} onChange={handleChange} required
              />
            </div>

            {/* End Time */}
            <div className="col-md-4">
              <label htmlFor="lessonEndTime" className="form-label">End Time</label>
              <input
                type="time" id="lessonEndTime" className="form-control"
                name="lessonEndTime" value={formData.lessonEndTime} onChange={handleChange} required
              />
            </div>

            {/* Location Autocomplete */}
            <div className="col-md-6">
              <label htmlFor="location-autocomplete" className="form-label">Location</label>
              <Autocomplete
                apiKey={googleApiKey}
                className="form-control"
                inputId="location-autocomplete" // Add id for label association
                options={{
                  types: ['geocode', 'establishment'],
                  fields: ['formatted_address','geometry','address_components']
                }}
                onPlaceSelected={place => {
                  if (place && place.geometry && place.geometry.location) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const loc = place.formatted_address || formData.location; // Fallback to existing if no formatted_address
                    const components = place.address_components ? extractFromAddress(
                      place.address_components,
                      [ 'street_number', 'route', 'locality', 'administrative_area_level_1', 'country', 'postal_code' ]
                    ) : {};
                    setFormData(fd => ({
                      ...fd,
                      latitude: lat,
                      longitude: lng,
                      location: loc,
                      metadata: JSON.stringify({ ...(JSON.parse(fd.metadata || '{}')), address_components: components }, null, 2)
                    }));
                  } else {
                     // Optionally clear fields if place is not valid, or handle as per UX requirements
                    console.warn("Place selected without geometry:", place);
                    // Allow manual input for location if Autocomplete doesn't provide a full address
                    // If you want to use the typed value directly if no place is selected:
                    // setFormData(fd => ({ ...fd, location: event.target.value }));
                  }
                }}
                // To allow manual typing and use it if no place is selected:
                onChange={(event) => handleChange({ target: { name: 'location', value: event.target.value } })}
                value={formData.location} // Bind the value to allow manual editing
              />
            </div>
            {/* Display Location (if needed separately from autocomplete input) */}
            <div className="col-md-6">
              <label htmlFor="location-text" className="form-label">Location (Text)</label>
              <input
                type="text" id="location-text" className="form-control"
                name="location" value={formData.location} onChange={handleChange}
                placeholder="Enter location manually or use autocomplete"
              />
            </div>


            {/* Latitude & Longitude (optional manual override) */}
            <div className="col-md-3">
              <label htmlFor="latitude" className="form-label">Latitude</label>
              <input
                type="number" step="any" id="latitude" className="form-control"
                name="latitude" value={formData.latitude} onChange={handleChange}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="longitude" className="form-label">Longitude</label>
              <input
                type="number" step="any" id="longitude" className="form-control"
                name="longitude" value={formData.longitude} onChange={handleChange}
              />
            </div>
            
            {/* Status */}
            <div className="col-md-6">
              <label htmlFor="status" className="form-label">Status</label>
              <select
                id="status" className="form-select"
                name="status" value={formData.status} onChange={handleChange}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Lesson Type ID */}
            {/* <div className="col-md-6">
              <label htmlFor="lessontype_id" className="form-label">Lesson Type ID</label>
              <input
                type="number" id="lessontype_id" className="form-control"
                name="lessontype_id" value={formData.lessontype_id} onChange={handleChange} required
              />
            </div>
*/}
            {/* Metadata JSON editor */}
            <div className="col-12">
              <label htmlFor="metadata" className="form-label">Metadata (JSON)</label>
              <textarea
                id="metadata" className="form-control"
                name="metadata" rows={4}
                value={formData.metadata} onChange={handleChange}
              />
            </div>
          </div>

          <button type="button" className="btn btn-primary mt-3" onClick={handleSave}>
            {editing ? 'Update Lesson' : 'Create Lesson'}
          </button>
          {editing && (
            <button type="button" className="btn btn-secondary mt-3 ms-2" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </div>
{selectedIds.length > 0 && (
  <div className="alert alert-info d-flex align-items-center justify-content-between">
    <div>{selectedIds.length} selected</div>
    <div className="d-flex gap-2">
      {/* quick mass update controls */}
      <select id="bulkStatus" className="form-select form-select-sm" defaultValue="">
        <option value="">Set status…</option>
        {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <input id="bulkLevel" className="form-control form-control-sm" placeholder="Set level…" />
      <button
        className="btn btn-sm btn-primary"
        onClick={async () => {
          const statusVal = document.getElementById('bulkStatus').value;
          const levelVal = document.getElementById('bulkLevel').value.trim();
          const patch = {};
          if (statusVal !== '') patch.status = parseInt(statusVal, 10);
          if (levelVal !== '') patch.level = levelVal;
          if (!Object.keys(patch).length) return alert('Choose at least one field to update.');
          try {
            await bulkUpdateExternalLessons(selectedIds, patch, access_token);
            setSelectedIds([]);
            fetchLessons();
          } catch (e) {
            alert(e?.response?.data?.message || 'Bulk update failed');
          }
        }}
      >
        Apply to selected
      </button>
      <button
        className="btn btn-sm btn-outline-danger"
        onClick={async () => {
          if (!window.confirm(`Delete ${selectedIds.length} lessons? This cannot be undone.`)) return;
          try {
            await bulkDeleteExternalLessons(selectedIds, access_token);
            setSelectedIds([]);
            fetchLessons();
          } catch (e) {
            alert(e?.response?.data?.message || 'Bulk delete failed');
          }
        }}
      >
        Delete selected
      </button>
    </div>
  </div>
)}

      {/* Lessons table */}
      <div className="table-responsive">
        <div className="card mb-3">
  <div className="card-body">
    <div className="row g-2 align-items-end">
      <div className="col-md-3">
        <label className="form-label">Search</label>
        <input className="form-control" placeholder="Title, URL, location, description…"
               value={q} onChange={e => { setPage(1); setQ(e.target.value); }} />
      </div>
      <div className="col-md-2">
        <label className="form-label">Status</label>
        <select className="form-select" value={statusFilter} onChange={e => { setPage(1); setStatusFilter(e.target.value); }}>
          <option value="">All</option>
          {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="col-md-2">
        <label className="form-label">Level</label>
        <input className="form-control" placeholder="e.g. 3.5" value={levelFilter} onChange={e => { setPage(1); setLevelFilter(e.target.value); }} />
      </div>
      <div className="col-md-2">
        <label className="form-label">From</label>
        <input type="datetime-local" className="form-control" value={dateFrom}
               onChange={e => { setPage(1); setDateFrom(e.target.value); }} />
      </div>
      <div className="col-md-2">
        <label className="form-label">To</label>
        <input type="datetime-local" className="form-control" value={dateTo}
               onChange={e => { setPage(1); setDateTo(e.target.value); }} />
      </div>
      <div className="col-md-1">
        <label className="form-label">Rows</label>
        <select className="form-select" value={perPage}
                onChange={e => { setPage(1); setPerPage(parseInt(e.target.value,10)); }}>
          {[10,20,50,100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  </div>
</div>

        <table className="table table-bordered table-hover">
          <thead className="table-light">
  <tr>
    <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
	    <th onClick={() => handleSort('id')} style={{cursor:'pointer'}}>ID {sortIcon('id')}</th>
	    <th onClick={() => handleSort('title')} style={{cursor:'pointer'}}>Title {sortIcon('title')}</th>
	    <th>Coach Name</th>
	    <th onClick={() => handleSort('start_date_time')} style={{cursor:'pointer'}}>Date & Time {sortIcon('start_date_time')}</th>
    <th onClick={() => handleSort('location')} style={{cursor:'pointer'}}>Location {sortIcon('location')}</th>
    <th onClick={() => handleSort('level')} style={{cursor:'pointer'}}>Level {sortIcon('level')}</th>
    <th>Lat,Long</th>
    <th onClick={() => handleSort('status')} style={{cursor:'pointer'}}>Status {sortIcon('status')}</th>
    <th onClick={() => handleSort('lessontype_id')} style={{cursor:'pointer'}}>Type ID {sortIcon('lessontype_id')}</th>
    <th>Booking URL</th>
    <th>Actions</th>
  </tr>
</thead>

          <tbody>
            {lessons.length > 0 ? lessons.map(lesson => (
              <tr key={lesson.id}>
	                <td>
	        <input
          type="checkbox"
          checked={selectedIds.includes(lesson.id)}
          onChange={() => toggleOne(lesson.id)}
        />
      </td>
	                <td>{lesson.id}</td>
	                <td>{lesson.title}</td>
	                <td>{lesson.metadata?.full_name || lesson.metadata?.coach_name || '-'}</td>
	                <td>
                  {lesson.start_date_time ? `${getLocalDate(lesson.start_date_time)} ${getLocalTime(lesson.start_date_time)}` : 'N/A'}
                  –
                  {lesson.end_date_time ? getLocalTime(lesson.end_date_time) : 'N/A'}
                </td>
                <td>{lesson.location}</td>
                <td>{lesson.level}</td>
                <td>{lesson.latitude ?? '-'}, {lesson.longitude ?? '-'}</td>
                <td>
                  {statusOptions.find(opt => opt.value === lesson.status)?.label || lesson.status}
                </td>
                <td>{lesson.lessontype_id}</td>
                <td>
                  <a href={lesson.external_url} target="_blank" rel="noopener noreferrer">Link</a>
                </td>
                <td>
                  <button className="btn btn-sm btn-outline-primary me-2 mb-1" onClick={() => handleEdit(lesson)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger mb-1" onClick={() => handleDelete(lesson.id)}>Delete</button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={12} className="text-center">No external lessons found.</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="d-flex justify-content-between align-items-center mt-3">
  <div className="text-muted">
    Page {page} of {totalPages} • {total} total
  </div>
  <div className="btn-group">
    <button className="btn btn-outline-secondary" disabled={page <= 1}
            onClick={() => setPage(p => Math.max(p - 1, 1))}>Prev</button>
    <button className="btn btn-outline-secondary" disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(p + 1, totalPages))}>Next</button>
  </div>
</div>

      </div>
        </>
      )}
    </main>
  );
};

export default ExternalLessons;
